// BILLING-PLANS in the in-memory API (docs/design/2026-10-27-wallet-pricing.md §3.5–§3.7, §6, §7):
// the Plan side of a company's Billing page and Operator › Billing, stateful, with the real
// routes' rules —
// - client routes (`/v1/companies/{co}/billing…`): owners, admins and an agency operator read;
//   members and client viewers get 404; only an owner or admin opens the portal, accepts a quote
//   or buys credit by invoice (an operator never pays with a client's money);
// - operator routes (`/v1/operator/billing/…`): agency users read the price lists; a platform
//   admin (an admin of the agency holding an operator grant on HTVA) writes them; a company's own
//   routes need an operator grant on it (404 otherwise).
// Nothing is invented: a company has no subscription, invoice or quote until the operator makes
// one here. The price lists start at Andrei's approved defaults (platform plan v2 $99 with 5
// people included and $15 per extra person, grace 14 days; maintenance Standard $499 cap 5,
// Advanced $999 cap 10, Managed $2,500 cap 25 supervised by the agency; a $1,500 template setup line;
// net 30, reminder day 7). Writes derive what Stripe would: putting a company on a plan bills
// its first period and previews the next invoice from its people and DEs; a tier change mid-
// period adds a proration line naming the DE; mark paid, void, quote send / accept and
// milestone invoices move the same rows the screens read.

import { routes } from "@companyos/schema/contracts";
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, page, problem, validation } from "./fake";
import { COMPANY_IDS, type FakeCompany, type FakeDe } from "./fake-data";
import type { MeResponse } from "./types";

type Overview = OperationOutput<"billing.overview">;
type InvoiceView = OperationOutput<"billing.invoices.get">;
type InvoiceLine = InvoiceView["lines"][number];
type Quote = OperationOutput<"billing.quotes.accept">;
type DraftLine = Quote["lines"][number];
type ProjectView = OperationOutput<"operator.billing.projects.create">;
type Prices = OperationOutput<"operator.billing.prices">;
type PlanRow = Prices["platform"][number];
type MaintRow = Prices["maintenance"][number];
type CatalogItem = Prices["catalog"][number];
type Defaults = Prices["defaults"];
type CompanyBilling = OperationOutput<"operator.billing.company">;
type DiscountView = CompanyBilling["discounts"][number];
type AgentLine = Overview["agents"][number];
type CompanyRow = OperationOutput<"operator.billing.companies">["data"][number];
type Upcoming = NonNullable<NonNullable<Overview["subscription"]>["upcoming"]>;
type Tier = MaintRow["tier"];
type PayMethod = Defaults["payment_methods"][number];
type Plan = Omit<PlanRow, "companies" | "current" | "byo_model_keys">;
type Maint = Omit<MaintRow, "des" | "current">;
type Invoice = Omit<InvoiceView, "display_status" | "grace_until">;
type Discount = Omit<DiscountView, "live">;
type BodyOf<I extends RouteId> = OperationInput<I> extends { body: infer B } ? B : never;

export const FAKE_BILLING_ROUTES = [
  "billing.overview",
  "billing.invoices.list",
  "billing.invoices.get",
  "billing.invoices.pdf",
  "billing.portal",
  "billing.quotes.list",
  "billing.quotes.pdf",
  "billing.quotes.accept",
  "billing.projects.list",
  "billing.topupInvoices.create",
  "operator.billing.prices",
  "operator.billing.plans.create",
  "operator.billing.plans.retire",
  "operator.billing.planModelAccess.put",
  "operator.billing.modelProviderMap.get",
  "operator.billing.modelProviderMap.put",
  "operator.billing.maintenancePlans.create",
  "operator.billing.maintenancePlans.retire",
  "operator.billing.catalog.create",
  "operator.billing.catalog.update",
  "operator.billing.catalog.delete",
  "operator.billing.defaults.put",
  "operator.billing.templates.put",
  "operator.billing.companies",
  "operator.billing.invoices.list",
  "operator.billing.company",
  "operator.billing.subscription.put",
  "operator.billing.subscription.retry",
  "operator.billing.subscription.cancel",
  "operator.billing.collection.put",
  "operator.billing.discounts.create",
  "operator.billing.discounts.end",
  "operator.billing.maintenance.put",
  "operator.billing.invoices.create",
  "operator.billing.invoices.send",
  "operator.billing.invoices.remind",
  "operator.billing.invoices.markPaid",
  "operator.billing.invoices.void",
  "operator.billing.quotes.list",
  "operator.billing.quotes.create",
  "operator.billing.quotes.pdf",
  "operator.billing.quotes.send",
  "operator.billing.quotes.cancel",
  "operator.billing.operations.retry",
  "operator.billing.operations.dismiss",
  "operator.billing.projects.create",
  "operator.billing.projects.update",
  "operator.billing.milestones.invoice",
  "operator.billing.provider.get",
  "operator.billing.provider.put",
  "operator.billing.close.get",
  "operator.billing.close.csv",
  "operator.billing.close.record",
  "operator.billing.ledger.failPayment",
  "operator.billing.ledger.renewNow",
] as const satisfies readonly RouteId[];

type Account = {
  collection_method: "card" | "invoice";
  net_terms_days: number | null;
  payment_methods: PayMethod[] | null;
  billing_email: string | null;
  pm_brand: string | null;
  pm_last4: string | null;
  setup_fee_minor: number | null;
  invoice_topups_allowed: boolean;
};

type Sub = {
  id: string;
  plan_id: string;
  status: NonNullable<Overview["subscription"]>["status"];
  base_price_override_minor: number | null;
  extra_seat_price_override_minor: number | null;
  included_seats_override: number | null;
  maintenance_overrides: CompanyBilling["overrides"]["maintenance_overrides"];
  current_period_start: string;
  current_period_end: string;
  trial_end: string | null;
  grace_days: number | null;
  past_due_since: string | null;
  grace_until: string | null;
  suspended_at: string | null;
  delinquent_invoice_id: string | null;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
};

type DeTier = { tier: Tier; plan_id: string; since: string };

type Book = {
  account: Account;
  sub: Sub | null;
  invoices: Invoice[];
  quotes: Quote[];
  discounts: Discount[];
  projects: { project: ProjectView["project"]; milestones: ProjectView["milestones"] }[];
  tiers: Map<string, DeTier>;
  /** Pending prorations (a tier change mid-period), on the next invoice. */
  prorations: InvoiceLine[];
  invoiceSeq: number;
  quoteSeq: number;
};

const DAY = 86_400_000;
const CURRENCY = "USD" as const;
const TIER_ORDER: Tier[] = ["standard", "advanced", "managed"];
const TIER_NAME: Record<Tier, string> = {
  standard: "Standard",
  advanced: "Advanced",
  managed: "Managed",
};

export function createBillingFake(deps: {
  now: () => Date;
  companies: readonly FakeCompany[];
  desOf: (companyId: string) => FakeDe[];
  /** Active person memberships (not DEs, not client viewers), as the caller may count them. */
  peopleCount: (me: MeResponse, companyId: string) => number;
  /** The live agents (AG) reporting to a DE. */
  agentsOf: (
    me: MeResponse,
    companyId: string,
    membershipId: string,
  ) => { id: string; name: string }[];
  /** The agent template heads (Plans › Templates). */
  templates: () => readonly {
    id: string;
    key: string;
    name: string;
    version: number;
    maintenance_tier: Tier | null;
    setup_fee_minor: number | null;
  }[];
  /** False as without `STRIPE_SECRET_KEY`; the twin plays Stripe's test mode by default. */
  stripeReady?: () => boolean;
}) {
  const at = () => deps.now().toISOString();
  const today = () => at().slice(0, 10);
  let seq = 0;
  const uid = () => `0193b111-0000-7000-8000-${(++seq).toString(16).padStart(12, "0")}`;
  const ready = () => deps.stripeReady?.() ?? true;
  const actor = { actor_kind: null, actor_membership_id: null };

  const addDays = (iso: string, n: number) =>
    new Date(new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso).getTime() + n * DAY)
      .toISOString()
      .slice(0, 10);
  const addMonths = (iso: string, n: number) => {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
    d.setUTCMonth(d.getUTCMonth() + n);
    return iso.length === 10 ? d.toISOString().slice(0, 10) : d.toISOString();
  };

  // Price lists ----------------------------------------------------------------------------------

  const seedAt = "2026-10-01T00:00:00.000Z";
  /** MODEL-KEYS-PLANS: plan keys whose companies may bring their own model keys. */
  const byoPlans = new Set<string>();
  /** MODEL-KEYS-PLANS: the model alias → provider map (the seeded defaults). */
  let providerMap: {
    alias: string;
    target: string;
    updated_by_user_id: null;
    updated_at: string;
  }[] = [
    { alias: "claude-haiku-5-5", target: "anthropic/claude-haiku-5-5" },
    { alias: "HERKO", target: "anthropic/claude-haiku-5-5" },
  ].map((x) => ({ ...x, updated_by_user_id: null, updated_at: "2026-10-01T00:00:00.000Z" }));
  /** The twin's gateway aliases in use; one has no mapping (shows as "Unmapped model"). */
  const gatewayAliases = ["HERKO", "qwen-light"];
  const providerMapView = () => ({
    entries: providerMap,
    unmapped: gatewayAliases.filter(
      (a) => !a.includes("/") && !providerMap.some((x) => x.alias === a),
    ),
  });
  const plans: Plan[] = [
    {
      id: uid(),
      key: "base",
      name: "Platform",
      base_price_minor: 9900,
      currency: CURRENCY,
      interval: "month",
      active: true,
      included_seats: 5,
      included_des: null,
      extra_seat_price_minor: 1500,
      stripe_price_id: "price_test_plan_base_v2",
      extra_seat_stripe_price_id: "price_test_plan_base_v2_seat",
      version: 2,
      effective_from: "2026-10-01",
      grace_days: 14,
      system_usage: "billable",
      created_by_user_id: null,
      created_at: seedAt,
    },
  ];
  const maint = (tier: Tier, price: number, cap: number, sup: "client" | "agency"): Maint => ({
    id: uid(),
    key: tier,
    name: TIER_NAME[tier],
    tier,
    price_minor: price,
    currency: CURRENCY,
    interval: "month",
    ag_cap: cap,
    supervision: sup,
    stripe_price_id: `price_test_maint_${tier}_v1`,
    version: 1,
    effective_from: "2026-10-01",
    active: true,
    created_by_user_id: null,
    created_at: seedAt,
  });
  const maintenance: Maint[] = [
    maint("standard", 49_900, 5, "client"),
    maint("advanced", 99_900, 10, "client"),
    maint("managed", 250_000, 25, "agency"),
  ];
  const catalog: CatalogItem[] = [
    {
      id: uid(),
      key: "setup-template-de",
      name: "Setup, template DE",
      kind: "setup",
      default_price_minor: 150_000,
      currency: CURRENCY,
      taxable: false,
      active: true,
      created_by_user_id: null,
      created_at: seedAt,
      updated_at: seedAt,
    },
  ];
  let defaults: Defaults = {
    key: "default",
    net_terms_days: 30,
    setup_fee_minor: 150_000,
    reminder_day: 7,
    payment_methods: ["card", "us_bank_account", "customer_balance"],
    updated_by_user_id: null,
    updated_at: seedAt,
  };
  /** Template id → its billing (absent = no tier, the default setup fee). */
  const templateBilling = new Map<
    string,
    { maintenance_tier: Tier | null; setup_fee_minor: number | null }
  >();

  /** The version a new subscription gets today: the latest started active one of the key. */
  const currentPlan = (key: string): Plan | null =>
    plans
      .filter((p) => p.key === key && p.active && p.effective_from <= today())
      .sort((a, b) => b.version - a.version)[0] ?? null;
  const currentMaint = (tier: Tier): Maint | null =>
    maintenance
      .filter((m) => m.tier === tier && m.active && m.effective_from <= today())
      .sort((a, b) => b.version - a.version)[0] ?? null;

  // Books ----------------------------------------------------------------------------------------

  const books = new Map<string, Book>();
  const bookOf = (companyId: string): Book => {
    let b = books.get(companyId);
    if (!b) {
      b = {
        account: {
          collection_method: "card",
          net_terms_days: null,
          payment_methods: null,
          billing_email: null,
          pm_brand: null,
          pm_last4: null,
          setup_fee_minor: null,
          invoice_topups_allowed: false,
        },
        sub: null,
        invoices: [],
        quotes: [],
        discounts: [],
        projects: [],
        tiers: new Map(),
        prorations: [],
        invoiceSeq: 0,
        quoteSeq: 0,
      };
      books.set(companyId, b);
    }
    return b;
  };
  const companyOf = (id: string) => deps.companies.find((c) => c.id === id) ?? null;
  const liveDes = (companyId: string) =>
    deps.desOf(companyId).filter((d) => d.status !== "retired");

  const netTerms = (b: Book) => b.account.net_terms_days ?? defaults.net_terms_days;
  const planOf = (b: Book) => plans.find((p) => p.id === b.sub?.plan_id) ?? null;
  const priceOfTier = (b: Book, tier: Tier) =>
    b.sub?.maintenance_overrides[tier] ?? currentMaint(tier)?.price_minor ?? 0;

  const terms = (b: Book) => {
    const plan = planOf(b);
    const sub = b.sub;
    if (!plan || !sub) return null;
    const base = sub.base_price_override_minor ?? plan.base_price_minor;
    const extra = sub.extra_seat_price_override_minor ?? plan.extra_seat_price_minor;
    const included = sub.included_seats_override ?? plan.included_seats;
    return {
      key: plan.key,
      name: plan.name,
      version: plan.version,
      interval: plan.interval,
      currency: plan.currency,
      base_price_minor: base,
      included_seats: included,
      extra_seat_price_minor: extra,
      grace_days: sub.grace_days ?? plan.grace_days,
      custom:
        sub.base_price_override_minor !== null ||
        sub.extra_seat_price_override_minor !== null ||
        sub.included_seats_override !== null ||
        Object.keys(sub.maintenance_overrides).length > 0,
    };
  };

  const isLive = (d: Discount, day = today()) =>
    d.starts_on <= day && day < d.ends_on && !(d.ended_at && d.ends_on <= day);
  const discountView = (d: Discount): DiscountView => ({ ...d, live: isLive(d) });
  /** The discount live today, else the next to start. */
  const currentDiscount = (b: Book) =>
    b.discounts.find((d) => isLive(d)) ??
    b.discounts
      .filter((d) => d.starts_on > today() && !d.ended_at)
      .sort((x, y) => x.starts_on.localeCompare(y.starts_on))[0] ??
    null;

  const displayStatus = (inv: Invoice): InvoiceView["display_status"] =>
    inv.status === "open" && inv.due_date !== null && inv.due_date < today()
      ? "overdue"
      : inv.status;
  const invoiceView = (b: Book, inv: Invoice): InvoiceView => {
    const display = displayStatus(inv);
    const grace = terms(b)?.grace_days ?? 14;
    return {
      ...inv,
      display_status: display,
      grace_until:
        display === "overdue" && inv.source === "subscription" && inv.due_date
          ? `${addDays(inv.due_date, grace)}T00:00:00.000Z`
          : null,
    };
  };
  const quoteView = (q: Quote): Quote =>
    q.status === "open" && q.expires_at < at() ? { ...q, status: "expired" } : q;

  /** An overdue subscription invoice makes the subscription past due (the daily sweep). */
  const sweep = (b: Book) => {
    const sub = b.sub;
    if (!sub || sub.status === "cancelled") return;
    if (sub.status === "trialing" && sub.trial_end && sub.trial_end <= at()) sub.status = "active";
    const late = b.invoices.find(
      (i) => i.source === "subscription" && displayStatus(i) === "overdue",
    );
    if (late && (sub.status === "active" || sub.status === "trialing")) {
      sub.status = "past_due";
      sub.past_due_since = `${late.due_date}T00:00:00.000Z`;
      sub.grace_until = `${addDays(late.due_date ?? today(), terms(b)?.grace_days ?? 14)}T00:00:00.000Z`;
      sub.delinquent_invoice_id = late.id;
    }
  };

  const agentLine = (me: MeResponse, companyId: string, b: Book, de: FakeDe): AgentLine => {
    const t = b.tiers.get(de.membership_id) ?? null;
    const plan = t ? (maintenance.find((m) => m.id === t.plan_id) ?? currentMaint(t.tier)) : null;
    const agents = deps.agentsOf(me, companyId, de.membership_id);
    return {
      membership_id: de.membership_id,
      name: de.display_name,
      status: de.status,
      tier: t?.tier ?? null,
      tier_name: t ? TIER_NAME[t.tier] : null,
      monthly_minor: t && b.sub ? priceOfTier(b, t.tier) : null,
      ag_count: agents.length,
      ag_cap: plan?.ag_cap ?? null,
      since: t?.since ?? null,
      agents,
      scheduled_tier: null,
      scheduled_at: null,
    };
  };
  const agentLines = (me: MeResponse, companyId: string, b: Book) =>
    liveDes(companyId).map((de) => agentLine(me, companyId, b, de));

  const line = (
    kind: InvoiceLine["kind"],
    description: string,
    quantity: number,
    unit: number | null,
    amount: number,
    extra: Partial<InvoiceLine> = {},
  ): InvoiceLine => ({
    id: `il_${uid().slice(-8)}`,
    kind,
    description,
    quantity,
    unit_amount_minor: unit,
    amount_minor: amount,
    proration: false,
    tier: null,
    period_start: null,
    period_end: null,
    membership_id: null,
    de_name: null,
    ...extra,
  });

  /** One period's recurring lines: the plan, extra people, maintenance per tier, retainers. */
  const recurringLines = (me: MeResponse, companyId: string, b: Book, from: string, to: string) => {
    const t = terms(b);
    if (!t) return [];
    const period = { period_start: from, period_end: to };
    const people = deps.peopleCount(me, companyId);
    const extraQty = Math.max(0, people - t.included_seats);
    const lines: InvoiceLine[] = [
      line("plan", `${t.name} plan`, 1, t.base_price_minor, t.base_price_minor, period),
    ];
    if (extraQty > 0)
      lines.push(
        line(
          "extra_seats",
          `Extra people × ${extraQty}`,
          extraQty,
          t.extra_seat_price_minor,
          extraQty * t.extra_seat_price_minor,
          period,
        ),
      );
    for (const tier of TIER_ORDER) {
      const qty = liveDes(companyId).filter(
        (d) => b.tiers.get(d.membership_id)?.tier === tier,
      ).length;
      if (!qty) continue;
      const unit = priceOfTier(b, tier);
      lines.push(
        line("maintenance", `Maintenance · ${TIER_NAME[tier]} × ${qty}`, qty, unit, qty * unit, {
          ...period,
          tier,
        }),
      );
    }
    for (const { project } of b.projects)
      if (project.maintenance_shape === "retainer" && project.retainer_minor)
        lines.push(
          line(
            "retainer",
            `Retainer · ${project.name}`,
            1,
            project.retainer_minor,
            project.retainer_minor,
            period,
          ),
        );
    return lines;
  };

  /** Lines with the discount applied as a negative line; subtotal, discount and total. */
  const withDiscount = (b: Book, lines: InvoiceLine[], day: string) => {
    const subtotal = lines.reduce((s, l) => s + l.amount_minor, 0);
    const d = b.discounts.find((x) => isLive(x, day));
    let discount = 0;
    if (d) {
      const applies = (l: InvoiceLine) =>
        d.applies_to === "all_recurring"
          ? true
          : d.applies_to === "plans"
            ? l.kind === "plan" || l.kind === "extra_seats"
            : l.kind === "maintenance" || (l.kind === "proration" && l.tier !== null);
      const base = lines.filter(applies).reduce((s, l) => s + l.amount_minor, 0);
      discount = Math.round((base * d.percent_bp) / 10_000);
      if (discount > 0)
        lines = [
          ...lines,
          line("discount", `${d.label}: ${d.percent_bp / 100}% off`, 1, null, -discount),
        ];
    }
    return { lines, subtotal, discount, total: subtotal - discount };
  };

  const upcomingOf = (me: MeResponse, companyId: string, b: Book): Upcoming | null => {
    const sub = b.sub;
    if (!sub || sub.status === "cancelled" || sub.cancel_at_period_end) return null;
    const from = sub.current_period_end;
    const to = addMonths(from, 1);
    const all = [...recurringLines(me, companyId, b, from, to), ...b.prorations];
    const priced = withDiscount(b, all, from.slice(0, 10));
    return {
      next_payment_at: from,
      subtotal_minor: priced.subtotal,
      discount_minor: priced.discount,
      total_minor: priced.total,
      currency: CURRENCY,
      lines: priced.lines,
      fetched_at: at(),
    };
  };

  const accountView = (b: Book): Overview["account"] => ({
    collection_method: b.account.collection_method,
    net_terms_days: netTerms(b),
    payment_methods: b.account.payment_methods ?? defaults.payment_methods,
    billing_email: b.account.billing_email,
    card_brand: b.account.pm_brand,
    card_last4: b.account.pm_last4,
    invoice_topups_allowed: b.account.invoice_topups_allowed,
  });

  const subView = (me: MeResponse, companyId: string, b: Book): Overview["subscription"] => {
    const sub = b.sub;
    const t = terms(b);
    if (!sub || !t) return null;
    const people = deps.peopleCount(me, companyId);
    const d = currentDiscount(b);
    return {
      id: sub.id,
      status: sub.status,
      plan: t,
      people_qty: people,
      extra_seat_qty: Math.max(0, people - t.included_seats),
      current_period_start: sub.current_period_start,
      current_period_end: sub.current_period_end,
      trial_end: sub.trial_end,
      past_due_since: sub.past_due_since,
      grace_until: sub.grace_until,
      suspended_at: sub.suspended_at,
      cancel_at_period_end: sub.cancel_at_period_end,
      discount: d ? discountView(d) : null,
      upcoming: upcomingOf(me, companyId, b),
      scheduled: null,
    };
  };

  const overview = (me: MeResponse, companyId: string, canManage: boolean): Overview => {
    const b = bookOf(companyId);
    sweep(b);
    return {
      account: accountView(b),
      subscription: subView(me, companyId, b),
      agents: agentLines(me, companyId, b),
      can_manage: canManage,
      stripe_ready: ready(),
      // the fake applies every change at once: nothing is ever pending
      pending_changes: 0,
    };
  };

  const projectView = (p: Book["projects"][number]): ProjectView => ({
    project: p.project,
    milestones: p.milestones,
    invoiced_minor: p.milestones
      .filter((m) => m.status !== "pending")
      .reduce((s, m) => s + m.amount_minor, 0),
    paid_minor: p.milestones
      .filter((m) => m.status === "paid")
      .reduce((s, m) => s + m.amount_minor, 0),
    settled_by_invoice_id: null,
  });

  const companyBilling = (me: MeResponse, companyId: string): CompanyBilling => {
    const b = bookOf(companyId);
    const sub = b.sub;
    return {
      ...overview(me, companyId, false),
      company_id: companyId,
      overrides: {
        base_price_override_minor: sub?.base_price_override_minor ?? null,
        extra_seat_price_override_minor: sub?.extra_seat_price_override_minor ?? null,
        included_seats_override: sub?.included_seats_override ?? null,
        maintenance_overrides: sub?.maintenance_overrides ?? {},
        grace_days: sub?.grace_days ?? null,
        setup_fee_minor: b.account.setup_fee_minor,
        net_terms_days: null,
        payment_methods: null,
      },
      discounts: b.discounts.map(discountView),
      projects: b.projects.map(projectView),
      operations: [],
    };
  };

  const companyRow = (me: MeResponse, c: FakeCompany): CompanyRow => {
    const b = bookOf(c.id);
    sweep(b);
    const plan = planOf(b);
    const open = b.invoices.filter((i) => i.status === "open");
    const d = currentDiscount(b);
    return {
      company_id: c.id,
      name: c.name,
      slug: c.slug,
      plan_key: plan?.key ?? null,
      plan_name: plan?.name ?? null,
      status: b.sub?.status ?? null,
      collection_method: b.account.collection_method,
      people_qty: deps.peopleCount(me, c.id),
      des: liveDes(c.id).length,
      next_invoice_minor: upcomingOf(me, c.id, b)?.total_minor ?? null,
      discount_label: d ? `${d.label} ${d.percent_bp / 100}%` : null,
      open_minor: open.reduce((s, i) => s + i.amount_due_minor, 0),
      overdue: open.filter((i) => displayStatus(i) === "overdue").length,
      grace_until: b.sub?.grace_until ?? null,
    };
  };

  // Invoices and quotes -------------------------------------------------------------------------

  const stripeUrls = (id: string) => ({
    hosted_url: `https://invoice.stripe.com/i/acct_test/${id}`,
    pdf_url: `https://pay.stripe.com/invoice/acct_test/${id}/pdf`,
  });
  const nextNumber = (companyId: string, b: Book) =>
    `${(companyOf(companyId)?.slug ?? "co").toUpperCase()}-${String(++b.invoiceSeq).padStart(4, "0")}`;

  const finalize = (companyId: string, b: Book, inv: Invoice, days?: number | null) => {
    const now = at();
    const due =
      inv.collection_method === "invoice" ? addDays(today(), days ?? netTerms(b)) : today();
    Object.assign(inv, {
      number: inv.number ?? nextNumber(companyId, b),
      status: "open",
      due_date: due,
      ...stripeUrls(inv.id),
      finalized_at: now,
      sent_at: now,
      updated_at: now,
    });
  };

  const makeInvoice = (
    companyId: string,
    b: Book,
    o: {
      source: Invoice["source"];
      lines: InvoiceLine[];
      memo?: string | null;
      days?: number | null;
      send: boolean;
      quote_id?: string | null;
      project_id?: string | null;
      milestone_id?: string | null;
      period?: { start: string; end: string };
      discount?: number;
      topup_micros?: number | null;
    },
  ): Invoice => {
    const now = at();
    const subtotal = o.lines
      .filter((l) => l.kind !== "discount")
      .reduce((s, l) => s + l.amount_minor, 0);
    const discount = o.discount ?? 0;
    const total = subtotal - discount;
    const inv: Invoice = {
      company_id: companyId,
      id: uid(),
      number: null,
      source: o.source,
      status: "draft",
      collection_method: b.account.collection_method,
      payment_method: null,
      currency: CURRENCY,
      subtotal_minor: subtotal,
      discount_minor: discount,
      tax_minor: 0,
      total_minor: total,
      amount_paid_minor: 0,
      amount_due_minor: total,
      due_date: null,
      period_start: o.period?.start ?? null,
      period_end: o.period?.end ?? null,
      hosted_url: null,
      pdf_url: null,
      lines: o.lines,
      memo: o.memo ?? null,
      quote_id: o.quote_id ?? null,
      project_id: o.project_id ?? null,
      milestone_id: o.milestone_id ?? null,
      topup_amount_micros: o.topup_micros ?? null,
      topup_credited_at: null,
      finalized_at: null,
      sent_at: null,
      paid_at: null,
      voided_at: null,
      reminded_at: null,
      payment_failed_at: null,
      overdue_at: null,
      paid_reference: null,
      marked_paid_by_user_id: null,
      ...actor,
      created_at: now,
      updated_at: now,
    };
    if (o.send) finalize(companyId, b, inv, o.days);
    b.invoices.unshift(inv);
    return inv;
  };

  const draftToLine = (d: DraftLine): InvoiceLine => {
    const item = d.catalog_key ? catalog.find((c) => c.key === d.catalog_key) : null;
    const kind: InvoiceLine["kind"] = d.milestone_id
      ? "milestone"
      : item?.kind === "setup"
        ? "setup"
        : item?.kind === "maintenance"
          ? "maintenance_yearly"
          : item?.kind === "build_milestone"
            ? "milestone"
            : "custom";
    return line(
      kind,
      d.description,
      d.quantity,
      d.unit_amount_minor,
      d.quantity * d.unit_amount_minor,
    );
  };

  const milestoneOf = (b: Book, id: string | null) => {
    if (!id) return null;
    for (const p of b.projects) {
      const m = p.milestones.find((x) => x.id === id);
      if (m) return { p, m };
    }
    return null;
  };

  const markPaid = (
    b: Book,
    inv: Invoice,
    paidAt: string,
    reference: string | null,
    by?: string,
  ) => {
    Object.assign(inv, {
      status: "paid",
      paid_at: paidAt,
      amount_paid_minor: inv.total_minor,
      amount_due_minor: 0,
      paid_reference: reference,
      marked_paid_by_user_id: by ?? null,
      payment_method: reference ? "out_of_band" : "card",
      updated_at: at(),
    });
    const ms = milestoneOf(b, inv.milestone_id);
    if (ms) Object.assign(ms.m, { status: "paid", updated_at: at() });
    if (b.sub && b.sub.delinquent_invoice_id === inv.id) restore(b);
  };

  const restore = (b: Book) => {
    if (!b.sub) return;
    Object.assign(b.sub, {
      status: "active",
      past_due_since: null,
      grace_until: null,
      suspended_at: null,
      delinquent_invoice_id: null,
    });
  };

  // Access ---------------------------------------------------------------------------------------

  type Client = { companyId: string; canManage: boolean; membershipId: string | null };
  const client = (ctx: Ctx): Client | Response => {
    const p = ctx.principal;
    if (!p?.manager) return notFound();
    return {
      companyId: p.company.id,
      canManage: p.roles.includes("owner") || p.roles.includes("admin"),
      membershipId:
        p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null,
    };
  };
  const managerOnly = () =>
    problem(403, "forbidden", "Forbidden", "Only an owner or an admin of the company does this.");

  const isPlatformAdmin = (me: MeResponse) =>
    me.agency?.role === "admin" &&
    me.agency.grants.some((g) => g.company_id === COMPANY_IDS.htva && g.level === "operator");
  /** An agency user reads the lists; null = allowed. */
  const agencyOnly = (me: MeResponse) => (me.agency ? null : notFound());
  const adminOnly = (me: MeResponse) =>
    !me.agency
      ? notFound()
      : isPlatformAdmin(me)
        ? null
        : problem(403, "forbidden", "Forbidden", "The price lists are a platform admin's.");
  const grantedCompanies = (me: MeResponse) =>
    (me.agency?.grants ?? [])
      .filter((g) => g.level === "operator")
      .map((g) => companyOf(g.company_id))
      .filter((c): c is FakeCompany => c !== null);
  const opCompany = (ctx: Ctx): { companyId: string; b: Book } | Response => {
    const id = ctx.params.company_id;
    if (!ctx.me.agency || !id) return notFound();
    if (!grantedCompanies(ctx.me).some((c) => c.id === id)) return notFound();
    return { companyId: id, b: bookOf(id) };
  };

  function parse<I extends RouteId>(id: I, ctx: Ctx): { data: BodyOf<I> } | Response {
    const schema = (routes[id] as { body?: { safeParse: (v: unknown) => unknown } }).body;
    if (!schema) return { data: undefined as BodyOf<I> };
    const r = schema.safeParse(ctx.body ?? {}) as {
      success: boolean;
      data?: unknown;
      error?: { issues: { path: unknown[]; message: string }[] };
    };
    if (!r.success)
      return validation(
        (r.error?.issues ?? [])
          .slice(0, 3)
          .map((i) => `${i.path.join(".")}: ${i.message}`)
          .join("; "),
      );
    return { data: r.data as BodyOf<I> };
  }
  const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);
  const notConfigured = (detail = "Stripe is not set up for this environment.") =>
    problem(409, "billing_not_configured", "Billing is not set up for this", detail);

  const planRow = (p: Plan): PlanRow => ({
    ...p,
    companies: [...books.values()].filter(
      (b) => b.sub && b.sub.plan_id === p.id && b.sub.status !== "cancelled",
    ).length,
    current: currentPlan(p.key)?.id === p.id,
    byo_model_keys: byoPlans.has(p.key),
  });
  const maintRow = (m: Maint): MaintRow => ({
    ...m,
    des: [...books.entries()].reduce(
      (n, [co, b]) =>
        n + liveDes(co).filter((d) => b.tiers.get(d.membership_id)?.plan_id === m.id).length,
      0,
    ),
    current: currentMaint(m.tier)?.id === m.id,
  });
  const templateRows = () =>
    deps.templates().map((t) => {
      const tb = templateBilling.get(t.id);
      return {
        template_id: t.id,
        key: t.key,
        name: t.name,
        version: t.version,
        maintenance_tier: tb ? tb.maintenance_tier : t.maintenance_tier,
        setup_fee_minor: tb ? tb.setup_fee_minor : t.setup_fee_minor,
      };
    });
  const prices = (me: MeResponse): Prices => ({
    platform: [...plans]
      .sort((a, b) => a.key.localeCompare(b.key) || b.version - a.version)
      .map(planRow),
    maintenance: [...maintenance]
      .sort(
        (a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier) || b.version - a.version,
      )
      .map(maintRow),
    catalog: catalog.filter((c) => c.active),
    templates: templateRows(),
    defaults,
    can_edit: isPlatformAdmin(me),
  });

  /** A DE's default tier: its template's, else Standard. */
  const defaultTier = (de: FakeDe): Tier => {
    const t = deps.templates().find((x) => x.key === de.template_key);
    const row = t ? templateRows().find((r) => r.template_id === t.id) : null;
    return row?.maintenance_tier ?? "standard";
  };

  // Handlers -------------------------------------------------------------------------------------

  const handlers: Partial<Record<RouteId, Handler>> = {
    "billing.overview": (ctx) => {
      const c = client(ctx);
      if (c instanceof Response) return c;
      return json(overview(ctx.me, c.companyId, c.canManage));
    },
    "billing.invoices.list": (ctx) => {
      const c = client(ctx);
      if (c instanceof Response) return c;
      const b = bookOf(c.companyId);
      sweep(b);
      const status = ctx.url.searchParams.get("status");
      const rows = b.invoices
        .filter((i) => i.status !== "draft")
        .map((i) => invoiceView(b, i))
        .filter((i) => !status || i.display_status === status);
      return json(page(rows, ctx.url));
    },
    "billing.invoices.get": (ctx) => {
      const c = client(ctx);
      if (c instanceof Response) return c;
      const b = bookOf(c.companyId);
      const inv = b.invoices.find((i) => i.id === ctx.params.invoice_id && i.status !== "draft");
      return inv ? json(invoiceView(b, inv)) : notFound();
    },
    "billing.invoices.pdf": (ctx) => {
      const c = client(ctx);
      if (c instanceof Response) return c;
      const inv = bookOf(c.companyId).invoices.find(
        (i) => i.id === ctx.params.invoice_id && i.status !== "draft",
      );
      return inv ? invoicePdf(inv) : notFound();
    },
    "billing.portal": (ctx) => {
      const c = client(ctx);
      if (c instanceof Response) return c;
      if (!c.canManage) return managerOnly();
      if (!ready()) return notConfigured();
      return json({ portal_url: `https://billing.stripe.com/p/session/test_${uid().slice(-12)}` });
    },
    "billing.quotes.list": (ctx) => {
      const c = client(ctx);
      if (c instanceof Response) return c;
      const b = bookOf(c.companyId);
      return json(page(b.quotes.filter((q) => q.status !== "draft").map(quoteView), ctx.url));
    },
    "billing.quotes.pdf": (ctx) => {
      const c = client(ctx);
      if (c instanceof Response) return c;
      const q = bookOf(c.companyId).quotes.find(
        (x) => x.id === ctx.params.quote_id && x.status !== "draft",
      );
      return q ? quotePdf(q) : notFound();
    },
    "billing.quotes.accept": (ctx) => {
      const c = client(ctx);
      if (c instanceof Response) return c;
      if (!c.canManage) return managerOnly();
      const b = bookOf(c.companyId);
      const q = b.quotes.find((x) => x.id === ctx.params.quote_id && x.status !== "draft");
      if (!q) return notFound();
      if (quoteView(q).status !== "open") return conflict("Only an open quote can be accepted.");
      const inv = makeInvoice(c.companyId, b, {
        source: "one_off",
        lines: q.lines.map(draftToLine),
        memo: q.memo,
        send: true,
        quote_id: q.id,
        project_id: q.project_id,
      });
      Object.assign(q, {
        status: "accepted",
        accepted_at: at(),
        accepted_by_membership_id: c.membershipId,
        invoice_id: inv.id,
        updated_at: at(),
      });
      const proj = b.projects.find((p) => p.project.id === q.project_id);
      if (proj) Object.assign(proj.project, { status: "accepted", updated_at: at() });
      return json(q);
    },
    "billing.projects.list": (ctx) => {
      const c = client(ctx);
      if (c instanceof Response) return c;
      return json({ data: bookOf(c.companyId).projects.map(projectView) });
    },
    "billing.topupInvoices.create": (ctx) => {
      const c = client(ctx);
      if (c instanceof Response) return c;
      if (!c.canManage) return managerOnly();
      const body = parse("billing.topupInvoices.create", ctx);
      if (body instanceof Response) return body;
      if (!ready()) return notConfigured();
      const b = bookOf(c.companyId);
      const cents = Math.floor(body.data.amount_micros / 10_000);
      const saved = b.account.collection_method;
      b.account.collection_method = "invoice";
      const inv = makeInvoice(c.companyId, b, {
        source: "topup",
        lines: [line("topup", "Prepaid credit", 1, cents, cents)],
        send: true,
        topup_micros: body.data.amount_micros,
      });
      b.account.collection_method = saved;
      return json(invoiceView(b, inv), 201);
    },

    // Operator: price lists ----------------------------------------------------------------------
    "operator.billing.prices": (ctx) => agencyOnly(ctx.me) ?? json(prices(ctx.me)),
    "operator.billing.plans.create": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const body = parse("operator.billing.plans.create", ctx);
      if (body instanceof Response) return body;
      const d = body.data;
      if (d.effective_from < today()) return conflict("A new version starts today or later.");
      const version =
        Math.max(0, ...plans.filter((p) => p.key === d.key).map((p) => p.version)) + 1;
      const p: Plan = {
        id: uid(),
        key: d.key,
        name: d.name,
        base_price_minor: d.base_price_minor,
        currency: CURRENCY,
        interval: d.interval ?? "month",
        active: true,
        included_seats: d.included_seats,
        included_des: d.included_des ?? null,
        extra_seat_price_minor: d.extra_seat_price_minor,
        stripe_price_id: `price_test_plan_${d.key}_v${version}`,
        extra_seat_stripe_price_id: `price_test_plan_${d.key}_v${version}_seat`,
        version,
        effective_from: d.effective_from,
        grace_days: d.grace_days,
        system_usage: d.system_usage ?? "billable",
        created_by_user_id: ctx.me.user.id,
        created_at: at(),
      };
      plans.push(p);
      return json(planRow(p), 201);
    },
    // MODEL-KEYS-PLANS: per plan (by key), whether its companies may bring their own model keys.
    "operator.billing.planModelAccess.put": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const key = String(ctx.params.plan_key);
      if (!plans.some((x) => x.key === key)) return notFound();
      const on = (ctx.body as { byo_model_keys?: unknown } | null)?.byo_model_keys === true;
      if (on) byoPlans.add(key);
      else byoPlans.delete(key);
      return json({
        plan_key: key,
        byo_model_keys: on,
        updated_by_user_id: null,
        updated_at: new Date().toISOString(),
      });
    },
    "operator.billing.modelProviderMap.get": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      return json(providerMapView());
    },
    "operator.billing.modelProviderMap.put": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const body = ctx.body as { entries?: { alias: string; target: string }[] } | null;
      const at = new Date().toISOString();
      const before = new Map(providerMap.map((x) => [x.alias, x]));
      providerMap = (body?.entries ?? [])
        .map((x) => {
          const was = before.get(x.alias);
          return was?.target === x.target
            ? was
            : { alias: x.alias, target: x.target, updated_by_user_id: null, updated_at: at };
        })
        .sort((a, b) => (a.alias < b.alias ? -1 : 1));
      return json(providerMapView());
    },
    "operator.billing.plans.retire": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const p = plans.find((x) => x.id === ctx.params.plan_id);
      if (!p) return notFound();
      if (planRow(p).companies > 0) return conflict("Companies are on this version.");
      p.active = false;
      return json(planRow(p));
    },
    "operator.billing.maintenancePlans.create": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const body = parse("operator.billing.maintenancePlans.create", ctx);
      if (body instanceof Response) return body;
      const d = body.data;
      if (d.effective_from < today()) return conflict("A new version starts today or later.");
      const version =
        Math.max(0, ...maintenance.filter((m) => m.tier === d.tier).map((m) => m.version)) + 1;
      const m: Maint = {
        id: uid(),
        key: d.tier,
        name: d.name,
        tier: d.tier,
        price_minor: d.price_minor,
        currency: CURRENCY,
        interval: "month",
        ag_cap: d.ag_cap,
        supervision: d.supervision,
        stripe_price_id: `price_test_maint_${d.tier}_v${version}`,
        version,
        effective_from: d.effective_from,
        active: true,
        created_by_user_id: ctx.me.user.id,
        created_at: at(),
      };
      maintenance.push(m);
      return json(maintRow(m), 201);
    },
    "operator.billing.maintenancePlans.retire": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const m = maintenance.find((x) => x.id === ctx.params.plan_id);
      if (!m) return notFound();
      if (maintRow(m).des > 0) return conflict("DEs are on this version.");
      m.active = false;
      return json(maintRow(m));
    },
    "operator.billing.catalog.create": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const body = parse("operator.billing.catalog.create", ctx);
      if (body instanceof Response) return body;
      const d = body.data;
      if (catalog.some((c) => c.key === d.key && c.active))
        return conflict(`A line with the key "${d.key}" exists.`);
      const item: CatalogItem = {
        id: uid(),
        key: d.key,
        name: d.name,
        kind: d.kind,
        default_price_minor: d.default_price_minor,
        currency: CURRENCY,
        taxable: d.taxable ?? false,
        active: true,
        created_by_user_id: ctx.me.user.id,
        created_at: at(),
        updated_at: at(),
      };
      catalog.push(item);
      return json(item, 201);
    },
    "operator.billing.catalog.update": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const item = catalog.find((c) => c.id === ctx.params.item_id && c.active);
      if (!item) return notFound();
      const body = parse("operator.billing.catalog.update", ctx);
      if (body instanceof Response) return body;
      Object.assign(item, body.data, { updated_at: at() });
      return json(item);
    },
    "operator.billing.catalog.delete": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const item = catalog.find((c) => c.id === ctx.params.item_id && c.active);
      if (!item) return notFound();
      Object.assign(item, { active: false, updated_at: at() });
      return json(item);
    },
    "operator.billing.defaults.put": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const body = parse("operator.billing.defaults.put", ctx);
      if (body instanceof Response) return body;
      defaults = {
        ...defaults,
        ...body.data,
        updated_by_user_id: ctx.me.user.id,
        updated_at: at(),
      };
      return json(defaults);
    },
    "operator.billing.templates.put": (ctx) => {
      const refused = adminOnly(ctx.me);
      if (refused) return refused;
      const id = ctx.params.template_id ?? "";
      if (!deps.templates().some((t) => t.id === id)) return notFound();
      const body = parse("operator.billing.templates.put", ctx);
      if (body instanceof Response) return body;
      templateBilling.set(id, body.data);
      return json(templateRows().find((t) => t.template_id === id));
    },

    // Operator: across companies -----------------------------------------------------------------
    "operator.billing.companies": (ctx) =>
      agencyOnly(ctx.me) ??
      json({ data: grantedCompanies(ctx.me).map((c) => companyRow(ctx.me, c)) }),
    "operator.billing.invoices.list": (ctx) => {
      const refused = agencyOnly(ctx.me);
      if (refused) return refused;
      const status = ctx.url.searchParams.get("status");
      const only = ctx.url.searchParams.get("company");
      const invoices: OperationOutput<"operator.billing.invoices.list">["invoices"] = [];
      const quotes: OperationOutput<"operator.billing.invoices.list">["quotes"] = [];
      for (const c of grantedCompanies(ctx.me)) {
        if (only && c.id !== only) continue;
        const b = bookOf(c.id);
        sweep(b);
        for (const i of b.invoices) {
          const v = invoiceView(b, i);
          if (!status || v.display_status === status)
            invoices.push({ ...v, company_name: c.name, company_slug: c.slug });
        }
        for (const q of b.quotes)
          quotes.push({ ...quoteView(q), company_name: c.name, company_slug: c.slug });
      }
      invoices.sort((a, b) => b.created_at.localeCompare(a.created_at));
      quotes.sort((a, b) => b.created_at.localeCompare(a.created_at));
      return json({ invoices, quotes, skipped: 0, next_cursor: null });
    },

    // Operator: one company ----------------------------------------------------------------------
    "operator.billing.company": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      return json(companyBilling(ctx.me, o.companyId));
    },
    "operator.billing.subscription.put": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const body = parse("operator.billing.subscription.put", ctx);
      if (body instanceof Response) return body;
      if (!ready()) return notConfigured();
      const d = body.data;
      const { b, companyId } = o;
      const plan = currentPlan(d.plan_key);
      if (!plan) return validation(`plan_key: no current plan "${d.plan_key}"`);
      const pick = <T>(v: T | undefined, keep: T): T => (v === undefined ? keep : v);
      const now = at();
      if (!b.sub || b.sub.status === "cancelled") {
        const trial = d.trial_end && d.trial_end > now ? d.trial_end : null;
        b.sub = {
          id: uid(),
          plan_id: plan.id,
          status: trial ? "trialing" : "active",
          base_price_override_minor: d.base_price_override_minor ?? null,
          extra_seat_price_override_minor: d.extra_seat_price_override_minor ?? null,
          included_seats_override: d.included_seats_override ?? null,
          maintenance_overrides: d.maintenance_overrides ?? {},
          current_period_start: now,
          current_period_end: addMonths(now, 1),
          trial_end: trial,
          grace_days: d.grace_days ?? null,
          past_due_since: null,
          grace_until: null,
          suspended_at: null,
          delinquent_invoice_id: null,
          cancel_at_period_end: false,
          cancelled_at: null,
        };
        for (const de of liveDes(companyId))
          if (!b.tiers.has(de.membership_id)) {
            const tier = defaultTier(de);
            const m = currentMaint(tier);
            if (m) b.tiers.set(de.membership_id, { tier, plan_id: m.id, since: today() });
          }
        for (const disc of b.discounts) if (!disc.applied_at) disc.applied_at = now;
        // The first period is billed at once (after a trial, at its end: nothing yet).
        if (!trial) {
          const lines = recurringLines(ctx.me, companyId, b, now, b.sub.current_period_end);
          const priced = withDiscount(b, lines, today());
          makeInvoice(companyId, b, {
            source: "subscription",
            lines: priced.lines,
            send: true,
            period: { start: now, end: b.sub.current_period_end },
            discount: priced.discount,
          });
        }
      } else {
        const sub = b.sub;
        // A new key moves the company now; the same key keeps its version until renewal.
        if (planOf(b)?.key !== d.plan_key) sub.plan_id = plan.id;
        sub.base_price_override_minor = pick(
          d.base_price_override_minor,
          sub.base_price_override_minor,
        );
        sub.extra_seat_price_override_minor = pick(
          d.extra_seat_price_override_minor,
          sub.extra_seat_price_override_minor,
        );
        sub.included_seats_override = pick(d.included_seats_override, sub.included_seats_override);
        sub.maintenance_overrides = pick(d.maintenance_overrides, sub.maintenance_overrides);
        sub.grace_days = pick(d.grace_days, sub.grace_days);
        if (d.trial_end !== undefined) {
          sub.trial_end = d.trial_end;
          if (sub.status === "trialing" && (!d.trial_end || d.trial_end <= now))
            sub.status = "active";
          if (sub.status === "active" && d.trial_end && d.trial_end > now) sub.status = "trialing";
        }
      }
      return json(companyBilling(ctx.me, companyId));
    },
    "operator.billing.subscription.retry": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const { b, companyId } = o;
      if (!b.sub || (b.sub.status !== "past_due" && b.sub.status !== "suspended"))
        return conflict("Only a past-due subscription is retried.");
      const inv = b.invoices.find((i) => i.id === b.sub?.delinquent_invoice_id);
      if (inv && inv.status === "open") markPaid(b, inv, at(), null);
      else restore(b);
      return json(companyBilling(ctx.me, companyId));
    },
    "operator.billing.subscription.cancel": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const body = parse("operator.billing.subscription.cancel", ctx);
      if (body instanceof Response) return body;
      const { b, companyId } = o;
      if (!b.sub || b.sub.status === "cancelled") return conflict("There is no live subscription.");
      if (body.data.at_period_end) b.sub.cancel_at_period_end = true;
      else Object.assign(b.sub, { status: "cancelled", cancelled_at: at() });
      return json(companyBilling(ctx.me, companyId));
    },
    "operator.billing.collection.put": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const body = parse("operator.billing.collection.put", ctx);
      if (body instanceof Response) return body;
      Object.assign(o.b.account, body.data);
      return json(companyBilling(ctx.me, o.companyId));
    },
    "operator.billing.discounts.create": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const body = parse("operator.billing.discounts.create", ctx);
      if (body instanceof Response) return body;
      const d = body.data;
      const starts = d.starts_on ?? today();
      const ends = addMonths(starts, d.months);
      const overlaps = o.b.discounts.some(
        (x) => !(x.ended_at && x.ends_on <= today()) && starts < x.ends_on && x.starts_on < ends,
      );
      if (overlaps)
        return conflict("A discount covers those months; start the new one when it ends.");
      const disc: Discount = {
        company_id: o.companyId,
        id: uid(),
        label: d.label ?? "Founding client",
        percent_bp: d.percent_bp,
        months: d.months,
        applies_to: d.applies_to,
        starts_on: starts,
        ends_on: ends,
        ended_at: null,
        applied_at: o.b.sub ? at() : null,
        ...actor,
        created_at: at(),
      };
      o.b.discounts.push(disc);
      return json(discountView(disc), 201);
    },
    "operator.billing.discounts.end": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const disc = o.b.discounts.find((x) => x.id === ctx.params.discount_id);
      if (!disc) return notFound();
      if (disc.ended_at || disc.ends_on <= today()) return conflict("The discount has ended.");
      const periodEnd = o.b.sub?.current_period_end.slice(0, 10) ?? today();
      // Removed at the next renewal, never mid-period; one not started yet ends now.
      const endsOn = disc.starts_on > today() ? disc.starts_on : periodEnd;
      Object.assign(disc, {
        ended_at: at(),
        ends_on: endsOn < disc.ends_on ? endsOn : disc.ends_on,
      });
      return json(discountView(disc));
    },
    "operator.billing.maintenance.put": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const body = parse("operator.billing.maintenance.put", ctx);
      if (body instanceof Response) return body;
      const { b, companyId } = o;
      const de = liveDes(companyId).find((x) => x.membership_id === ctx.params.membership_id);
      if (!de) return notFound();
      const sub = b.sub;
      if (!sub || sub.status === "cancelled")
        return notConfigured("Put the company on a plan first.");
      const tier = body.data.tier;
      const m = currentMaint(tier);
      if (!m) return conflict(`No current ${TIER_NAME[tier]} version.`);
      const agents = deps.agentsOf(ctx.me, companyId, de.membership_id).length;
      if (agents > m.ag_cap)
        return conflict(
          `${de.display_name} has ${agents} AGs; ${TIER_NAME[tier]} allows ${m.ag_cap}.`,
        );
      const before = b.tiers.get(de.membership_id);
      if (before?.tier !== tier) {
        if (before) {
          const end = new Date(sub.current_period_end).getTime();
          const days = Math.max(0, Math.ceil((end - deps.now().getTime()) / DAY));
          const diff = priceOfTier(b, tier) - priceOfTier(b, before.tier);
          const amount = Math.round((diff * days) / 30);
          if (amount !== 0)
            b.prorations.push(
              line(
                "proration",
                `${de.display_name}, ${TIER_NAME[before.tier]} → ${TIER_NAME[tier]}, ${days} days`,
                1,
                null,
                amount,
                {
                  proration: true,
                  tier,
                  membership_id: de.membership_id,
                  de_name: de.display_name,
                },
              ),
            );
        }
        b.tiers.set(de.membership_id, { tier, plan_id: m.id, since: before?.since ?? today() });
      }
      return json(agentLine(ctx.me, companyId, b, de));
    },
    "operator.billing.invoices.create": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const body = parse("operator.billing.invoices.create", ctx);
      if (body instanceof Response) return body;
      if (!ready()) return notConfigured();
      const d = body.data;
      for (const l of d.lines)
        if (l.milestone_id && !milestoneOf(o.b, l.milestone_id))
          return validation("lines: a milestone is not this company's");
      const inv = makeInvoice(o.companyId, o.b, {
        source: d.source ?? "one_off",
        lines: d.lines.map(draftToLine),
        memo: d.memo ?? null,
        days: d.days_until_due ?? null,
        send: d.send,
      });
      for (const l of d.lines) {
        const ms = milestoneOf(o.b, l.milestone_id);
        if (ms) Object.assign(ms.m, { status: "invoiced", invoice_id: inv.id, updated_at: at() });
      }
      return json(invoiceView(o.b, inv), 201);
    },
    "operator.billing.invoices.send": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const inv = o.b.invoices.find((i) => i.id === ctx.params.invoice_id);
      if (!inv) return notFound();
      if (inv.status !== "draft") return conflict("Only a draft is sent.");
      finalize(o.companyId, o.b, inv);
      return json(invoiceView(o.b, inv));
    },
    "operator.billing.invoices.remind": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const inv = o.b.invoices.find((i) => i.id === ctx.params.invoice_id);
      if (!inv) return notFound();
      if (inv.status !== "open") return conflict("Only an open invoice gets a reminder.");
      Object.assign(inv, { reminded_at: at(), updated_at: at() });
      return json(invoiceView(o.b, inv));
    },
    // BILLING-LEDGER: who takes the company's money, the month-end close, ledger dunning/renewal
    "operator.billing.provider.get": (ctx) => {
      const o = opCompany(ctx);
      return o instanceof Response ? o : json(providerView(o.companyId, o.b));
    },
    "operator.billing.provider.put": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const body = parse("operator.billing.provider.put", ctx);
      if (body instanceof Response) return body;
      const d = body.data;
      const st = providerOf(o.companyId);
      if (d.provider !== undefined) {
        if (d.provider === "stripe" && !ready()) return notConfigured();
        st.selected = d.provider;
      }
      if (d.system_usage !== undefined) st.system_usage_override = d.system_usage;
      if (d.bill_voice_usage !== undefined) st.bill_voice_usage = d.bill_voice_usage;
      if (d.voice_markup_bp !== undefined) st.voice_markup_bp = d.voice_markup_bp;
      if (d.voice_free_rate_micros !== undefined)
        st.voice_free_rate_micros = d.voice_free_rate_micros;
      if (d.voice_self_hosted_rate_micros !== undefined)
        st.voice_self_hosted_rate_micros = d.voice_self_hosted_rate_micros;
      return json(providerView(o.companyId, o.b));
    },
    "operator.billing.close.get": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const w = closeWorksheet(o.companyId, o.b, ctx.params.period_key ?? "");
      return w ? json(w) : validation("period_key: YYYY-MM");
    },
    "operator.billing.close.csv": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const w = closeWorksheet(o.companyId, o.b, ctx.params.period_key ?? "");
      if (!w) return validation("period_key: YYYY-MM");
      const rows = [
        "kind,key,cost_micros,wallet_micros",
        ...Object.entries(w.totals_by_category).map(
          ([k, v]) => `total,${k},${v.cost_micros},${v.wallet_micros}`,
        ),
        `invoices,total_minor,${w.reconciliation.invoice_total_minor},`,
      ];
      return new Response(`${rows.join("\n")}\n`, {
        status: 200,
        headers: { "content-type": "application/octet-stream" },
      });
    },
    "operator.billing.close.record": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const key = ctx.params.period_key ?? "";
      const w = closeWorksheet(o.companyId, o.b, key);
      if (!w) return validation("period_key: YYYY-MM");
      if (w.closed) return conflict("That month is already closed.");
      if (w.ends_at > at()) return conflict("Only an ended month is closed.");
      closes.set(`${o.companyId}:${key}`, {
        closed_at: at(),
        by_user_id: ctx.me.user.id,
        totals_by_category: w.totals_by_category,
        unresolved_count: 0,
        late_count: 0,
        invoice_total_minor: w.reconciliation.invoice_total_minor,
        worksheet_sha256: sha256ish(JSON.stringify({ ...w, closed: null })),
      });
      return json(closeWorksheet(o.companyId, o.b, key));
    },
    "operator.billing.ledger.failPayment": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const inv = o.b.invoices.find((i) => i.id === ctx.params.invoice_id);
      if (!inv) return notFound();
      if (providerKind(o.companyId) !== "ledger")
        return conflict("The company is not on the ledger.");
      if (inv.status !== "open") return conflict("Only an open invoice can fail its payment.");
      const sub = o.b.sub;
      if (sub && inv.source === "subscription" && sub.status === "active") {
        const grace = terms(o.b)?.grace_days ?? 14;
        Object.assign(sub, {
          status: "past_due",
          past_due_since: at(),
          grace_until: `${addDays(today(), grace)}T00:00:00.000Z`,
          delinquent_invoice_id: inv.id,
        });
      }
      Object.assign(inv, { updated_at: at() });
      return json(invoiceView(o.b, inv));
    },
    "operator.billing.ledger.renewNow": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const { b, companyId } = o;
      if (providerKind(companyId) !== "ledger")
        return conflict("The company is not on the ledger.");
      const sub = b.sub;
      if (!sub || (sub.status !== "active" && sub.status !== "past_due"))
        return conflict("There is no live ledger subscription to renew.");
      const now = at();
      const end = addMonths(now, 1);
      Object.assign(sub, { current_period_start: now, current_period_end: end });
      const priced = withDiscount(b, recurringLines(ctx.me, companyId, b, now, end), today());
      makeInvoice(companyId, b, {
        source: "subscription",
        lines: priced.lines,
        send: true,
        period: { start: now, end },
        discount: priced.discount,
      });
      return json(companyBilling(ctx.me, companyId));
    },
    "operator.billing.invoices.markPaid": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const inv = o.b.invoices.find((i) => i.id === ctx.params.invoice_id);
      if (!inv) return notFound();
      const body = parse("operator.billing.invoices.markPaid", ctx);
      if (body instanceof Response) return body;
      if (inv.status !== "open") return conflict("Only an open invoice is marked paid.");
      markPaid(o.b, inv, `${body.data.paid_on}T12:00:00.000Z`, body.data.reference, ctx.me.user.id);
      return json(invoiceView(o.b, inv));
    },
    "operator.billing.invoices.void": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const inv = o.b.invoices.find((i) => i.id === ctx.params.invoice_id);
      if (!inv) return notFound();
      if (inv.status !== "open" && inv.status !== "draft")
        return conflict("Only an open or draft invoice is voided.");
      Object.assign(inv, {
        status: "void",
        voided_at: at(),
        amount_due_minor: 0,
        updated_at: at(),
      });
      for (const p of o.b.projects)
        for (const m of p.milestones)
          if (m.invoice_id === inv.id)
            Object.assign(m, { status: "pending", invoice_id: null, updated_at: at() });
      if (o.b.sub?.delinquent_invoice_id === inv.id) restore(o.b);
      return json(invoiceView(o.b, inv));
    },
    "operator.billing.quotes.list": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      return json({ data: o.b.quotes.map(quoteView) });
    },
    "operator.billing.quotes.create": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const body = parse("operator.billing.quotes.create", ctx);
      if (body instanceof Response) return body;
      if (!ready()) return notConfigured();
      const d = body.data;
      if (d.expires_on < today()) return validation("expires_on: the quote would be expired");
      if (d.project_id && !o.b.projects.some((p) => p.project.id === d.project_id))
        return validation("project_id: not this company's project");
      const now = at();
      const q: Quote = {
        company_id: o.companyId,
        id: uid(),
        number: null,
        title: d.title,
        status: "draft",
        lines: d.lines,
        total_minor: d.lines.reduce((s, l) => s + l.quantity * l.unit_amount_minor, 0),
        currency: CURRENCY,
        expires_at: `${d.expires_on}T23:59:59.000Z`,
        memo: d.memo ?? null,
        pdf_url: null,
        hosted_url: null,
        invoice_id: null,
        project_id: d.project_id ?? null,
        sent_at: null,
        accepted_at: null,
        accepted_by_membership_id: null,
        canceled_at: null,
        ...actor,
        created_at: now,
        updated_at: now,
      };
      if (d.send) sendQuote(o.companyId, o.b, q);
      o.b.quotes.unshift(q);
      return json(q, 201);
    },
    "operator.billing.quotes.pdf": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const q = o.b.quotes.find((x) => x.id === ctx.params.quote_id && x.status !== "draft");
      return q ? quotePdf(q) : notFound();
    },
    // the in-memory billing applies every change at once: nothing ever waits or fails
    "operator.billing.operations.retry": (ctx) => {
      const o = opCompany(ctx);
      return o instanceof Response ? o : notFound();
    },
    "operator.billing.operations.dismiss": (ctx) => {
      const o = opCompany(ctx);
      return o instanceof Response ? o : notFound();
    },
    "operator.billing.quotes.send": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const q = o.b.quotes.find((x) => x.id === ctx.params.quote_id);
      if (!q) return notFound();
      if (q.status !== "draft") return conflict("Only a draft quote is sent.");
      sendQuote(o.companyId, o.b, q);
      return json(q);
    },
    "operator.billing.quotes.cancel": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const q = o.b.quotes.find((x) => x.id === ctx.params.quote_id);
      if (!q) return notFound();
      if (quoteView(q).status !== "draft" && quoteView(q).status !== "open")
        return conflict("Only a draft or open quote is cancelled.");
      Object.assign(q, { status: "canceled", canceled_at: at(), updated_at: at() });
      return json(q);
    },
    "operator.billing.projects.create": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const body = parse("operator.billing.projects.create", ctx);
      if (body instanceof Response) return body;
      const d = body.data;
      const shape = shapeProblem(d.maintenance_shape, d.maintenance_pct_bp, d.retainer_minor);
      if (shape) return shape;
      const now = at();
      const projectId = uid();
      const project: ProjectView["project"] = {
        company_id: o.companyId,
        id: projectId,
        name: d.name,
        kind: d.kind,
        status: d.quote_id ? "quoted" : "accepted",
        quote_id: d.quote_id ?? null,
        total_minor: d.total_minor,
        currency: CURRENCY,
        maintenance_shape: d.maintenance_shape,
        maintenance_pct_bp:
          d.maintenance_shape === "yearly_pct" ? (d.maintenance_pct_bp ?? null) : null,
        retainer_minor: d.maintenance_shape === "retainer" ? (d.retainer_minor ?? null) : null,
        delivered_on: null,
        next_maintenance_on: null,
        ...actor,
        created_at: now,
        updated_at: now,
      };
      const milestones = d.milestones.map((m, i) => ({
        company_id: o.companyId,
        id: uid(),
        project_id: projectId,
        seq: i + 1,
        name: m.name,
        amount_minor: m.amount_minor,
        due_on: m.due_on,
        status: "pending" as const,
        invoice_id: null,
        ...actor,
        created_at: now,
        updated_at: now,
      }));
      const entry = { project, milestones };
      o.b.projects.push(entry);
      return json(projectView(entry), 201);
    },
    "operator.billing.projects.update": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const entry = o.b.projects.find((p) => p.project.id === ctx.params.project_id);
      if (!entry) return notFound();
      const body = parse("operator.billing.projects.update", ctx);
      if (body instanceof Response) return body;
      const d = body.data;
      const shapeNow = d.maintenance_shape ?? entry.project.maintenance_shape;
      const pct =
        d.maintenance_pct_bp !== undefined
          ? d.maintenance_pct_bp
          : entry.project.maintenance_pct_bp;
      const retainer =
        d.retainer_minor !== undefined ? d.retainer_minor : entry.project.retainer_minor;
      const shape = shapeProblem(shapeNow, pct, retainer);
      if (shape) return shape;
      const delivered =
        d.delivered_on !== undefined
          ? d.delivered_on
          : d.status === "delivered" && !entry.project.delivered_on
            ? today()
            : entry.project.delivered_on;
      Object.assign(entry.project, {
        ...(d.status ? { status: d.status } : {}),
        delivered_on: delivered,
        maintenance_shape: shapeNow,
        maintenance_pct_bp: shapeNow === "yearly_pct" ? pct : null,
        retainer_minor: shapeNow === "retainer" ? retainer : null,
        next_maintenance_on:
          shapeNow === "yearly_pct" && delivered ? addMonths(delivered, 12) : null,
        updated_at: at(),
      });
      return json(projectView(entry));
    },
    "operator.billing.milestones.invoice": (ctx) => {
      const o = opCompany(ctx);
      if (o instanceof Response) return o;
      const entry = o.b.projects.find((p) => p.project.id === ctx.params.project_id);
      const m = entry?.milestones.find((x) => x.id === ctx.params.milestone_id);
      if (!entry || !m) return notFound();
      const body = parse("operator.billing.milestones.invoice", ctx);
      if (body instanceof Response) return body;
      if (m.status !== "pending") return conflict("The milestone is invoiced already.");
      if (!ready()) return notConfigured();
      const inv = makeInvoice(o.companyId, o.b, {
        source: "milestone",
        lines: [
          line("milestone", `${entry.project.name} · ${m.name}`, 1, m.amount_minor, m.amount_minor),
        ],
        send: body.data.send,
        project_id: entry.project.id,
        milestone_id: m.id,
      });
      Object.assign(m, { status: "invoiced", invoice_id: inv.id, updated_at: at() });
      if (entry.project.status === "accepted")
        Object.assign(entry.project, { status: "in_progress", updated_at: at() });
      return json(invoiceView(o.b, inv), 201);
    },
  };

  // BILLING-LEDGER state: the operator's provider choice and the months closed ------------------
  type ProviderKind = "stripe" | "ledger";
  type ProviderState = {
    selected: ProviderKind | null;
    system_usage_override: OperationOutput<"operator.billing.provider.get">["system_usage_override"];
    bill_voice_usage: boolean;
    voice_markup_bp: number;
    voice_free_rate_micros: number;
    voice_self_hosted_rate_micros: number;
  };
  type PeriodClose = NonNullable<OperationOutput<"operator.billing.close.get">["closed"]>;
  const providers = new Map<string, ProviderState>();
  const closes = new Map<string, PeriodClose>();
  const providerOf = (companyId: string): ProviderState => {
    let s = providers.get(companyId);
    if (!s) {
      s = {
        selected: null,
        system_usage_override: null,
        bill_voice_usage: true,
        voice_markup_bp: 10_000,
        voice_free_rate_micros: 0,
        voice_self_hosted_rate_micros: 30_000,
      };
      providers.set(companyId, s);
    }
    return s;
  };
  /** The default rule: Stripe when it is set up, else the ledger. */
  const providerKind = (companyId: string): ProviderKind =>
    providerOf(companyId).selected ?? (ready() ? "stripe" : "ledger");

  const providerView = (
    companyId: string,
    b: Book,
  ): OperationOutput<"operator.billing.provider.get"> => {
    const st = providerOf(companyId);
    const kind = providerKind(companyId);
    const sub = b.sub;
    return {
      company_id: companyId,
      provider: kind,
      selected: st.selected,
      stripe_configured: ready(),
      ledger_enabled: true,
      ledger:
        kind === "ledger"
          ? {
              has_customer: sub !== null && sub !== undefined,
              subscription_status: sub?.status ?? null,
              current_period_end: sub?.current_period_end ?? null,
              next_renewal_at: sub && sub.status !== "cancelled" ? sub.current_period_end : null,
              open_invoices: b.invoices.filter((i) => i.status === "open").length,
              unpaid_top_ups: b.invoices.filter(
                (i) => i.status === "open" && i.topup_amount_micros !== null,
              ).length,
              undelivered_events: 0,
            }
          : null,
      system_usage: st.system_usage_override ?? "billable",
      system_usage_override: st.system_usage_override,
      bill_voice_usage: st.bill_voice_usage,
      voice_markup_bp: st.voice_markup_bp,
      voice_free_rate_micros: st.voice_free_rate_micros,
      voice_self_hosted_rate_micros: st.voice_self_hosted_rate_micros,
    };
  };

  /** A stand-in fingerprint: 64 hex digits from the text (the real one is SHA-256). */
  const sha256ish = (text: string) => {
    let out = "";
    for (let k = 0; out.length < 64; k++) {
      let h = 0x811c9dc5 ^ k;
      for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193);
      out += (h >>> 0).toString(16).padStart(8, "0");
    }
    return out;
  };

  /** The month-end worksheet from the book's invoices (no metered usage exists in this fake). */
  const closeWorksheet = (
    companyId: string,
    b: Book,
    key: string,
  ): OperationOutput<"operator.billing.close.get"> | null => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(key)) return null;
    const starts = `${key}-01T00:00:00.000Z`;
    const ends = addMonths(starts, 1);
    const invoices = b.invoices.filter(
      (i) => i.status !== "draft" && i.created_at >= starts && i.created_at < ends,
    );
    const bySource: Record<string, number> = {};
    for (const i of invoices) bySource[i.source] = (bySource[i.source] ?? 0) + i.total_minor;
    const total = invoices.reduce((s, i) => s + i.total_minor, 0);
    return {
      company_id: companyId,
      period_key: key,
      starts_at: starts,
      ends_at: ends,
      requests: [],
      lines: [],
      totals_by_category: {},
      wallet: {
        opening_micros: 0,
        topups_micros: 0,
        bonus_micros: 0,
        usage_micros: 0,
        refunds_micros: 0,
        adjustments_micros: 0,
        reversals_micros: 0,
        closing_micros: 0,
        usage_by_category: {},
      },
      invoices: invoices.map((i) => ({
        invoice_id: i.id,
        source: i.source,
        status: i.status,
        total_minor: i.total_minor,
        topup_micros: i.topup_amount_micros ?? null,
      })),
      unresolved: [],
      late_adjustments: [],
      reconciliation: {
        invoice_total_minor: total,
        invoices_by_source: bySource,
        topup_invoiced_micros: 0,
        wallet_topups_micros: 0,
        usage_invoice_lines: 0,
        wallet_usage_micros: 0,
        ok: true,
        problems: [],
      },
      closed: closes.get(`${companyId}:${key}`) ?? null,
    };
  };

  /** A ledger invoice's PDF (an in-memory stand-in for the ledger's rendering). */
  function invoicePdf(inv: Invoice) {
    return new Response(
      `%PDF-1.4\n% CompanyOS in-memory invoice ${inv.number ?? inv.id}\n%%EOF\n`,
      {
        status: 200,
        headers: { "content-type": "application/pdf" },
      },
    );
  }

  /** A sent quote's PDF (an in-memory stand-in for Stripe's rendering). */
  function quotePdf(q: Quote) {
    return new Response(`%PDF-1.4\n% CompanyOS in-memory quote ${q.number ?? q.id}\n%%EOF\n`, {
      status: 200,
      headers: { "content-type": "application/pdf" },
    });
  }

  function sendQuote(companyId: string, b: Book, q: Quote) {
    const slug = (companyOf(companyId)?.slug ?? "co").toUpperCase();
    Object.assign(q, {
      status: "open",
      number: q.number ?? `Q-${slug}-${String(++b.quoteSeq).padStart(4, "0")}`,
      // as the API answers: no hosted page; the PDF through the API (Stripe renders it)
      hosted_url: null,
      pdf_url: `/v1/companies/${companyId}/billing/quotes/${q.id}/pdf`,
      sent_at: at(),
      updated_at: at(),
    });
  }

  function shapeProblem(
    shape: ProjectView["project"]["maintenance_shape"],
    pct: number | null | undefined,
    retainer: number | null | undefined,
  ): Response | null {
    if (shape === "yearly_pct" && !pct)
      return validation("maintenance_pct_bp: a yearly maintenance needs its percentage");
    if (shape === "retainer" && !retainer)
      return validation("retainer_minor: a retainer needs its monthly amount");
    return null;
  }

  /** MODEL-KEYS-PLANS: the company's live plan key and whether its plan allows own model keys. */
  const modelAccessOf = (companyId: string) => {
    const s = books.get(companyId)?.sub;
    const key =
      s && s.status !== "cancelled" ? (plans.find((p) => p.id === s.plan_id)?.key ?? null) : null;
    return { planKey: key, allowed: key !== null && byoPlans.has(key) };
  };

  return { handlers, modelAccessOf };
}
